/**
 * 게임 밸런스 수치 모음.
 *
 * 숫자의 소스는 `balance.json` 이다. 이 파일은 타입·핫리로드·라벨만 담당한다.
 * 게임 로직에는 하드코딩된 수치를 남기지 않는다.
 */

import seed from './balance.json';
import { emitDataReloadSoon, replaceArray, requireRecord } from './catalog';

export const WORLD = { ...seed.WORLD };
export const PLAYER = {
  ...seed.PLAYER,
  hitKnockback: { ...seed.PLAYER.hitKnockback },
};
export const STATS = structuredClone(seed.STATS);
export type PrimaryStat = 'str' | 'dex' | 'int' | 'luk';

export const PRIMARY_STATS: readonly PrimaryStat[] = ['str', 'dex', 'int', 'luk'];

export const PRIMARY_STAT_LABEL: Record<PrimaryStat, string> = {
  str: 'STR',
  dex: 'DEX',
  int: 'INT',
  luk: 'LUK',
};

export const PRIMARY_STAT_HINT: Record<PrimaryStat, string> = {
  str: '최대 HP · 공격력',
  dex: '공격력 · 방어력 · 명중 · 회피',
  int: '최대 MP',
  luk: '크리티컬 · 명중 · 회피',
};

export const EXP_TABLE: number[] = [...seed.EXP_TABLE];
export const PROGRESSION = { ...seed.PROGRESSION };
export const ADVANCEMENT = { ...seed.ADVANCEMENT };
export const COMBAT = {
  ...seed.COMBAT,
  shake: { ...seed.COMBAT.shake },
};

export type SkillKind = 'melee' | 'projectile' | 'buff';

export interface SkillDef {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  icon: string;
  mpCost: number;
  damageMultiplier: number;
  hits: number;
  maxTargets: number;
  hitbox: { width: number; height: number; offsetX: number; offsetY: number };
  cooldownMs: number;
  castMs: number;
  hitFrameRatio: number;
  knockback: number;
  lungeVelocity: number;
  anim: 'attack' | 'swing';
  effect: 'slash' | 'power' | 'blast' | 'bolt' | 'arrow' | 'star' | 'buff';
  kind: SkillKind;
  projectile?: { texture: string; anim: string; speed: number };
  buff?: { id: string; durationMs: number; defenseMul?: number; damageMul?: number; moveMul?: number };
}

export const BASIC_ATTACK = structuredClone({
  ...seed.BASIC_ATTACK,
  kind: 'melee' as SkillKind,
}) as SkillDef;

export type RangedBasicStyle = 'bolt' | 'arrow' | 'star';

export interface RangedBasicSpec {
  name: string;
  nameEn: string;
  description: string;
  icon: string;
  effect: SkillDef['effect'];
  lungeVelocity: number;
  knockback: number;
  projectile: NonNullable<SkillDef['projectile']>;
}

export const BASIC_RANGED = structuredClone(seed.BASIC_RANGED) as Record<
  RangedBasicStyle,
  RangedBasicSpec
>;
export const DROPS = {
  ...seed.DROPS,
  popVelocity: { ...seed.DROPS.popVelocity },
};
export const RESPAWN = { ...seed.RESPAWN };
export const AUDIO = { ...seed.AUDIO };
export const NET = { ...seed.NET };

export function applyBalance(raw: unknown): void {
  const data = requireRecord(raw, 'balance.json');
  Object.assign(WORLD, requireRecord(data.WORLD, 'WORLD'));
  const player = requireRecord(data.PLAYER, 'PLAYER');
  Object.assign(PLAYER, player);
  if (player.hitKnockback) Object.assign(PLAYER.hitKnockback, player.hitKnockback);
  Object.assign(STATS, requireRecord(data.STATS, 'STATS'));
  if (!Array.isArray(data.EXP_TABLE)) throw new Error('balance.json: EXP_TABLE 배열이 아닙니다');
  replaceArray(EXP_TABLE, data.EXP_TABLE as number[]);
  Object.assign(PROGRESSION, requireRecord(data.PROGRESSION, 'PROGRESSION'));
  if (data.ADVANCEMENT) Object.assign(ADVANCEMENT, requireRecord(data.ADVANCEMENT, 'ADVANCEMENT'));
  if (EXP_TABLE.length < STATS.maxLevel) {
    throw new Error(`balance.json: EXP_TABLE 길이 ${EXP_TABLE.length} < maxLevel ${STATS.maxLevel}`);
  }
  const combat = requireRecord(data.COMBAT, 'COMBAT');
  Object.assign(COMBAT, combat);
  if (combat.shake) Object.assign(COMBAT.shake, combat.shake);
  Object.assign(BASIC_ATTACK, requireRecord(data.BASIC_ATTACK, 'BASIC_ATTACK'));
  BASIC_ATTACK.kind = 'melee';
  const ranged = requireRecord(data.BASIC_RANGED, 'BASIC_RANGED');
  for (const key of ['bolt', 'arrow', 'star'] as const) {
    Object.assign(BASIC_RANGED[key], requireRecord(ranged[key], `BASIC_RANGED.${key}`));
  }
  const drops = requireRecord(data.DROPS, 'DROPS');
  Object.assign(DROPS, drops);
  if (drops.popVelocity) Object.assign(DROPS.popVelocity, drops.popVelocity);
  Object.assign(RESPAWN, requireRecord(data.RESPAWN, 'RESPAWN'));
  Object.assign(AUDIO, requireRecord(data.AUDIO, 'AUDIO'));
  Object.assign(NET, requireRecord(data.NET, 'NET'));
}

if (import.meta.hot) {
  import.meta.hot.accept('./balance.json', (mod) => {
    if (!mod?.default) return;
    applyBalance(mod.default);
    emitDataReloadSoon();
  });
}
